import React from 'react';
import './ElectricalServices.css';
import SEO from '../../components/SEO';

const ElectricalServices = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Electrical Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Techie Interiors",
      "telephone": "+254721431126",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Nairobi",
        "addressCountry": "KE"
      }
    },
    "areaServed": "Kenya",
    "description": "Professional electrical services in Nairobi Kenya including wiring, lighting installation, system upgrades, and maintenance for residential and commercial properties."
  };

  return (
    <div className="electrical-services-page">
      {/* SEO Component */}
      <SEO
        title="Professional Electrical Services in Nairobi Kenya | Techie Interiors"
        description="Certified electrical services in Nairobi Kenya - wiring, lighting installation, system upgrades, and maintenance for homes and businesses. Safe, efficient, and modern electrical solutions."
        image="/techLogo.jpeg"
        url="https://techieinteriors.co.ke/electrical"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="electrical-hero">
        <div className="hero-overlay">
          <div className="hero-content">
            <h1>Professional Electrical Services for Homes & Businesses</h1>
            <p>Comprehensive electrical solutions designed to ensure safety, efficiency, and modern functionality in your space. From wiring to lighting installation, our certified electricians deliver high-quality results.</p>
            <a href="services#contact" className="cta-button">Get a Free Quote</a>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="electrical-intro">
        <div className="container">
          <p className="intro-text">
            At Techie Interiors, we provide comprehensive electrical services designed to ensure safety, efficiency, and modern functionality in your space. Whether you're building a new property, renovating, or upgrading outdated systems, our certified electricians deliver high-quality solutions tailored to your needs.
          </p>
          <p className="intro-text">
            From electrical wiring and lighting installation to system upgrades and maintenance, we guarantee reliable performance, energy efficiency, and compliance with all safety standards.
          </p>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-electrical">
        <div className="container">
          <h2>Why Choose Our Electrical Services?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">✅</div>
              <h3>Certified and experienced electricians</h3>
            </div>
            <div className="feature-card">
              <div className="feature-icon">✅</div>
              <h3>Safe, code-compliant installations</h3>
            </div>
            <div className="feature-card">
              <div className="feature-icon">✅</div>
              <h3>Energy-efficient solutions (LED & smart systems)</h3>
            </div>
            <div className="feature-card">
              <div className="feature-icon">✅</div>
              <h3>Residential & commercial expertise</h3>
            </div>
            <div className="feature-card">
              <div className="feature-icon">✅</div>
              <h3>Fast, reliable, and affordable service</h3>
            </div>
          </div>
          <p className="feature-note">We combine technical expertise with modern design integration, ensuring your electrical systems complement your interior spaces perfectly.</p>
        </div>
      </section>

      {/* Services Section */}
      <section className="electrical-services">
        <div className="container">
          <h2>Our Complete Electrical Services</h2>
          
          {/* Lighting Design & Installation */}
          <div className="service-block">
            <h3>Lighting Design & Installation</h3>
            <p>We design and install lighting systems that enhance both functionality and aesthetics.</p>
            <p>Our lighting solutions include:</p>
            <ul>
              <li>LED lighting installation (energy-efficient)</li>
              <li>Ambient and mood lighting</li>
              <li>Decorative and feature lighting</li>
              <li>Office and commercial lighting systems</li>
              <li>Smart lighting automation</li>
            </ul>
            <p className="service-note">💡 Whether you want a modern, minimal look or a luxurious ambiance, we create lighting that transforms your space.</p>
          </div>

          {/* Complete Electrical Installations */}
          <div className="service-block">
            <h3>Complete Electrical Installations</h3>
            <p>We handle full electrical system installations from start to finish.</p>
            <p>Our services cover:</p>
            <ul>
              <li>Electrical wiring for new buildings</li>
              <li>Socket and switch installation</li>
              <li>Distribution board setup</li>
              <li>Power supply connections</li>
              <li>Electrical system planning and layout</li>
            </ul>
            <p>All installations are done in compliance with industry safety standards, ensuring long-term reliability.</p>
          </div>

          {/* Electrical Upgrades & Renovations */}
          <div className="service-block">
            <h3>Electrical Upgrades & Renovations</h3>
            <p>Outdated systems can be unsafe and inefficient. We provide modern upgrades to improve safety and performance.</p>
            <p>Upgrade services include:</p>
            <ul>
              <li>Rewiring old buildings</li>
              <li>Circuit breaker and panel upgrades</li>
              <li>Smart home electrical integration</li>
              <li>Appliance installation and connection</li>
              <li>Energy-saving system upgrades</li>
            </ul>
          </div>

          {/* Electrical Maintenance & Repairs */}
          <div className="service-block">
            <h3>Electrical Maintenance & Repairs</h3>
            <p>We offer routine maintenance and emergency repairs to keep your systems running smoothly.</p>
            <p>Our maintenance services include:</p>
            <ul>
              <li>Fault detection and troubleshooting</li>
              <li>Electrical repairs and replacements</li>
              <li>Preventive maintenance checks</li>
              <li>Power failure solutions</li>
            </ul>
            <p className="service-note">⚡ Quick response ensures minimal downtime and maximum safety.</p>
          </div>

          {/* Power Distribution & Load Management */}
          <div className="service-block">
            <h3>Power Distribution & Load Management</h3>
            <p>Efficient power distribution is key to safety and performance.</p>
            <p>We provide:</p>
            <ul>
              <li>Load balancing solutions</li>
              <li>Power distribution systems</li>
              <li>Backup power integration (generators/inverters)</li>
              <li>Surge protection systems</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Residential & Commercial Solutions */}
      <section className="residential-commercial">
        <div className="container">
          <h2>Residential & Commercial Electrical Solutions</h2>
          <p>We serve a wide range of clients:</p>
          
          <div className="solutions-grid">
            <div className="solution-card">
              <h3>Residential Electrical Services</h3>
              <ul>
                <li>Home wiring and lighting</li>
                <li>Kitchen and wardrobe electrical fittings</li>
                <li>Smart home installations</li>
              </ul>
            </div>
            <div className="solution-card">
              <h3>Commercial Electrical Services</h3>
              <ul>
                <li>Office electrical systems</li>
                <li>Retail and showroom lighting</li>
                <li>Large-scale power distribution</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="electrical-process">
        <div className="container">
          <h2>Our Process</h2>
          <div className="process-steps">
            <div className="step">
              <div className="step-number">1</div>
              <h4>Consultation & Assessment</h4>
              <p>Understanding your needs</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h4>Design & Planning</h4>
              <p>Creating efficient electrical layouts</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h4>Installation</h4>
              <p>Safe and professional execution</p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h4>Testing & Quality Check</h4>
              <p>Ensuring full functionality</p>
            </div>
            <div className="step">
              <div className="step-number">5</div>
              <h4>Maintenance Support</h4>
              <p>Ongoing service and support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Compliance */}
      <section className="safety-compliance">
        <div className="container">
          <h2>Safety & Compliance</h2>
          <p>At Techie Interiors, safety is our priority. All our electrical work:</p>
          <ul className="safety-list">
            <li>Meets local and international electrical codes</li>
            <li>Uses high-quality certified materials</li>
            <li>Is tested for safety and durability</li>
          </ul>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="electrical-faq">
        <div className="container">
          <h2>Frequently Asked Questions (FAQs)</h2>
          <div className="faq-list">
            <div className="faq-item">
              <h4>Q1: Do you offer electrical services for both homes and offices?</h4>
              <p>Yes, we provide electrical solutions for residential, commercial, and industrial spaces.</p>
            </div>
            <div className="faq-item">
              <h4>Q2: Can you upgrade old wiring systems?</h4>
              <p>Absolutely. We specialize in rewiring and upgrading outdated electrical systems.</p>
            </div>
            <div className="faq-item">
              <h4>Q3: Do you install energy-efficient lighting?</h4>
              <p>Yes, we provide LED and smart lighting solutions to reduce energy consumption.</p>
            </div>
            <div className="faq-item">
              <h4>Q4: How do I know if my electrical system needs an upgrade?</h4>
              <p>Frequent power outages, flickering lights, or outdated panels are signs you need an upgrade.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="electrical-cta">
        <div className="container">
          <h2>Ready to Upgrade Your Electrical Systems?</h2>
          <p>Contact Techie Interiors today for professional, safe, and efficient electrical services tailored to your needs.</p>
          <a href="services#contact" className="cta-button">Get Your Free Quote</a>
        </div>
      </section>

      {/* SEO Footer Section */}
      <section className="electrical-seo-footer">
        <div className="container">
          <h2>Electrical Services | Wiring, Lighting Installation & Electrical Upgrades | Techie Interiors</h2>
          <p>
            Professional electrical services for homes and businesses. We offer wiring, lighting installation, electrical upgrades, maintenance, and power distribution. 
            Safe, certified, and reliable solutions by Techie Interiors.
          </p>
          <p>
            Our comprehensive electrical services cover residential and commercial properties, ensuring all installations meet the highest safety standards. 
            From LED lighting installation to complete rewiring projects, our certified electricians deliver quality workmanship and energy-efficient solutions.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ElectricalServices;